import type { UseQueryResult } from "@tanstack/react-query"
import { useQuery } from "@tanstack/react-query"

import { queryKeys } from "~/api/cache"

import type { ArcadiaGreenButtonSiteDataResponseDto } from "../dto/arcadia-green-button.dto"
import { arcadiaGreenButtonService } from "../services/arcadia-green-button.service"

export const ARCADIA_GREEN_BUTTON_POLL_INTERVAL_MS = 2_000

const TERMINAL_FAILURE_STATUSES = new Set([`failed`, `cancelled`, `expired`])
const ACTIVE_REFRESH_STATUS_DETAILS = new Set([
  `refresh_requested`,
  `refresh_waiting_for_intervals`,
  `retry_requested`,
])

const shouldStopPolling = (
  data: ArcadiaGreenButtonSiteDataResponseDto | undefined,
): boolean => {
  const connection = data?.connection

  if (!connection) {
    return false
  }

  if (TERMINAL_FAILURE_STATUSES.has(connection.status)) {
    return true
  }

  if (connection.status !== `imported`) {
    return false
  }

  if (
    connection.statusDetail &&
    ACTIVE_REFRESH_STATUS_DETAILS.has(connection.statusDetail)
  ) {
    return false
  }

  const currentImport = data?.currentImport
  const application = data?.currentProposalApplication
  const proposalApplicationInProgress = application?.outcome === `processing`

  if (
    connection.statusDetail === `refresh_usage_captured` &&
    !proposalApplicationInProgress
  ) {
    return true
  }

  const terminalApplication =
    application?.outcome === `applied` ||
    (application?.outcome === `skipped` &&
      !(
        application.reason === `manual_lock` &&
        !data?.isCurrentProposalUsageLocked
      ))
  // A complete import on this Site applies to its proposals on its own, so the
  // drawer waits for that to land. A connection reused from another project
  // never will: applying it here is the seller's explicit choice, so there is
  // nothing to wait for and polling would run forever.
  const completeImportAwaitingApplication = Boolean(
    !data?.sharedFromSiteId &&
      currentImport?.quality === `full` &&
      currentImport.hasFullYearCoverage &&
      !terminalApplication,
  )
  return !completeImportAwaitingApplication && !proposalApplicationInProgress
}

export const useFetchArcadiaGreenButtonSiteData = (
  siteId: string,
  proposalId: string,
  options: { enabled?: boolean } = {},
): UseQueryResult<ArcadiaGreenButtonSiteDataResponseDto, unknown> => {
  return useQuery({
    queryKey: queryKeys.arcadiaGreenButton.proposal(siteId, proposalId),
    queryFn: () => arcadiaGreenButtonService.fetchSiteData(siteId, proposalId),
    enabled: options.enabled ?? true,
    refetchInterval: (data) =>
      data?.connection && !shouldStopPolling(data)
        ? ARCADIA_GREEN_BUTTON_POLL_INTERVAL_MS
        : false,
  })
}
